/**
 * ISP CRM Ticket Categories Model
 *
 * NOTE: These are app-specific types for ISP ticket classification.
 * There are no corresponding library types in @open/nexus-public-api.
 *
 * This module provides:
 * - Main categories (actions, consultations, complaints)
 * - Sub-categories for each main category
 * - Required fields per category
 * - Default SLA and priority settings
 * - Localization (RU/EN) for category labels
 */

// Основные разделы обращений
export enum TicketMainCategory {
  ACTIONS = 'actions',                       // Действия
  CONSULTATIONS = 'consultations',           // Консультации
  SUGGESTIONS = 'suggestions',               // Пожелания
  COMPLAINTS_ORAL = 'complaints_oral',       // Претензии устные
  COMPLAINTS_WRITTEN = 'complaints_written'  // Претензии письменные
}

// Подкатегории для каждого раздела
export enum ActionSubCategory {
  CONNECTION = 'connection',               // Подключение
  DISCONNECTION = 'disconnection',         // Отключение
  TRANSFER = 'transfer',                   // Переоформление
  RECALCULATION = 'recalculation',         // Перерасчёт
  TARIFF_CHANGE = 'tariff_change',         // Изменение тарифа
  EQUIPMENT_REPLACE = 'equipment_replace'  // Замена оборудования
}

export enum ConsultationSubCategory {
  SERVICES_INFO = 'services_info',     // Информация об услугах
  TARIFFS_INFO = 'tariffs_info',       // Информация о тарифах
  PROMOTIONS = 'promotions',           // Акции
  COVERAGE_ZONE = 'coverage_zone',     // Зона покрытия
  OFFICE_ADDRESSES = 'office_addresses' // Адреса офисов
}

export enum SuggestionSubCategory {
  SERVICE_QUALITY = 'service_quality', // Качество услуг
  NEW_SERVICES = 'new_services',       // Новые услуги
  OTHER = 'other'                      // Другое
}

export enum ComplaintOralSubCategory {
  INTERNET_QUALITY = 'internet_quality',   // Качество интернета
  STAFF_BEHAVIOR = 'staff_behavior',       // Работа сотрудников
  INCORRECT_BILLING = 'incorrect_billing'  // Некорректное начисление
}

export enum ComplaintWrittenSubCategory {
  OFFICIAL_COMPLAINT = 'official_complaint', // Официальная жалоба
  REFUND_REQUEST = 'refund_request',         // Заявление на возврат
  CONTRACT_DISPUTE = 'contract_dispute'      // Спор по договору
}

// Объединённый тип подкатегорий
export type TicketSubCategory =
  | ActionSubCategory
  | ConsultationSubCategory
  | SuggestionSubCategory
  | ComplaintOralSubCategory
  | ComplaintWrittenSubCategory

// Интерфейс категории обращения
export interface TicketCategory {
  id: string
  mainCategory: TicketMainCategory
  subCategory: TicketSubCategory
  name: string
  nameRu: string
  description?: string
  requiredFields: RequiredField[]
  defaultQueueId?: string
  defaultPriority: 'high' | 'mid' | 'low'
  slaHours: number
  notificationTemplateId?: string
  isActive: boolean
}

// Обязательные поля для категории
export interface RequiredField {
  key: string
  label: string
  labelRu: string
  type: 'text' | 'number' | 'phone' | 'email' | 'file' | 'select' | 'address'
  required: boolean
  options?: { value: string; label: string }[]
}

// Выбранная категория
export interface SelectedCategory {
  mainCategory: TicketMainCategory
  subCategory: TicketSubCategory
}

// Маппинг подкатегорий по основным разделам
export const ISP_SUBCATEGORIES: Record<TicketMainCategory, TicketSubCategory[]> = {
  [TicketMainCategory.ACTIONS]: Object.values(ActionSubCategory),
  [TicketMainCategory.CONSULTATIONS]: Object.values(ConsultationSubCategory),
  [TicketMainCategory.SUGGESTIONS]: Object.values(SuggestionSubCategory),
  [TicketMainCategory.COMPLAINTS_ORAL]: Object.values(ComplaintOralSubCategory),
  [TicketMainCategory.COMPLAINTS_WRITTEN]: Object.values(ComplaintWrittenSubCategory)
}

// Локализация основных категорий
export const MAIN_CATEGORY_LABELS: Record<TicketMainCategory, string> = {
  [TicketMainCategory.ACTIONS]: 'Действия',
  [TicketMainCategory.CONSULTATIONS]: 'Консультации',
  [TicketMainCategory.SUGGESTIONS]: 'Пожелания',
  [TicketMainCategory.COMPLAINTS_ORAL]: 'Претензии устные',
  [TicketMainCategory.COMPLAINTS_WRITTEN]: 'Претензии письменные'
}

// Локализация подкатегорий
export const SUB_CATEGORY_LABELS: Record<TicketSubCategory, string> = {
  // Действия
  [ActionSubCategory.CONNECTION]: 'Подключение',
  [ActionSubCategory.DISCONNECTION]: 'Отключение',
  [ActionSubCategory.TRANSFER]: 'Переоформление',
  [ActionSubCategory.RECALCULATION]: 'Перерасчёт',
  [ActionSubCategory.TARIFF_CHANGE]: 'Изменение тарифа',
  [ActionSubCategory.EQUIPMENT_REPLACE]: 'Замена оборудования',
  // Консультации
  [ConsultationSubCategory.SERVICES_INFO]: 'Информация об услугах',
  [ConsultationSubCategory.TARIFFS_INFO]: 'Информация о тарифах',
  [ConsultationSubCategory.PROMOTIONS]: 'Акции',
  [ConsultationSubCategory.COVERAGE_ZONE]: 'Зона покрытия',
  [ConsultationSubCategory.OFFICE_ADDRESSES]: 'Адреса офисов',
  // Пожелания
  [SuggestionSubCategory.SERVICE_QUALITY]: 'Качество услуг',
  [SuggestionSubCategory.NEW_SERVICES]: 'Новые услуги',
  [SuggestionSubCategory.OTHER]: 'Другое',
  // Претензии устные
  [ComplaintOralSubCategory.INTERNET_QUALITY]: 'Качество интернета',
  [ComplaintOralSubCategory.STAFF_BEHAVIOR]: 'Работа сотрудников',
  [ComplaintOralSubCategory.INCORRECT_BILLING]: 'Некорректное начисление',
  // Претензии письменные
  [ComplaintWrittenSubCategory.OFFICIAL_COMPLAINT]: 'Официальная жалоба',
  [ComplaintWrittenSubCategory.REFUND_REQUEST]: 'Заявление на возврат',
  [ComplaintWrittenSubCategory.CONTRACT_DISPUTE]: 'Спор по договору'
}

// Цвета для категорий (Tailwind CSS classes)
export const MAIN_CATEGORY_COLORS: Record<TicketMainCategory, string> = {
  [TicketMainCategory.ACTIONS]: 'text-blue-600 dark:text-blue-400',
  [TicketMainCategory.CONSULTATIONS]: 'text-green-600 dark:text-green-400',
  [TicketMainCategory.SUGGESTIONS]: 'text-yellow-600 dark:text-yellow-400',
  [TicketMainCategory.COMPLAINTS_ORAL]: 'text-orange-600 dark:text-orange-400',
  [TicketMainCategory.COMPLAINTS_WRITTEN]: 'text-red-600 dark:text-red-400'
}

// Background colors для категорий
export const MAIN_CATEGORY_BG_COLORS: Record<TicketMainCategory, string> = {
  [TicketMainCategory.ACTIONS]: 'bg-blue-100 dark:bg-blue-950/50',
  [TicketMainCategory.CONSULTATIONS]: 'bg-green-100 dark:bg-green-950/50',
  [TicketMainCategory.SUGGESTIONS]: 'bg-yellow-100 dark:bg-yellow-950/50',
  [TicketMainCategory.COMPLAINTS_ORAL]: 'bg-orange-100 dark:bg-orange-950/50',
  [TicketMainCategory.COMPLAINTS_WRITTEN]: 'bg-red-100 dark:bg-red-950/50'
}

// Обязательные поля по умолчанию для категорий
export const DEFAULT_REQUIRED_FIELDS: Record<TicketMainCategory, RequiredField[]> = {
  [TicketMainCategory.ACTIONS]: [
    { key: 'accountNumber', label: 'Account Number', labelRu: 'Лицевой счёт', type: 'text', required: true },
    { key: 'address', label: 'Connection Address', labelRu: 'Адрес подключения', type: 'address', required: true },
    { key: 'phone', label: 'Contact Phone', labelRu: 'Контактный телефон', type: 'phone', required: true }
  ],
  [TicketMainCategory.CONSULTATIONS]: [
    { key: 'phone', label: 'Contact Phone', labelRu: 'Контактный телефон', type: 'phone', required: true }
  ],
  [TicketMainCategory.SUGGESTIONS]: [
    { key: 'phone', label: 'Contact Phone', labelRu: 'Контактный телефон', type: 'phone', required: false }
  ],
  [TicketMainCategory.COMPLAINTS_ORAL]: [
    { key: 'accountNumber', label: 'Account Number', labelRu: 'Лицевой счёт', type: 'text', required: true },
    { key: 'phone', label: 'Contact Phone', labelRu: 'Контактный телефон', type: 'phone', required: true }
  ],
  [TicketMainCategory.COMPLAINTS_WRITTEN]: [
    { key: 'accountNumber', label: 'Account Number', labelRu: 'Лицевой счёт', type: 'text', required: true },
    { key: 'fullName', label: 'Full Name', labelRu: 'ФИО', type: 'text', required: true },
    { key: 'document', label: 'Application Scan', labelRu: 'Скан заявления', type: 'file', required: true }
  ]
}

// Приоритеты по умолчанию для категорий
export const DEFAULT_PRIORITIES: Record<TicketMainCategory, 'high' | 'mid' | 'low'> = {
  [TicketMainCategory.ACTIONS]: 'mid',
  [TicketMainCategory.CONSULTATIONS]: 'low',
  [TicketMainCategory.SUGGESTIONS]: 'low',
  [TicketMainCategory.COMPLAINTS_ORAL]: 'mid',
  [TicketMainCategory.COMPLAINTS_WRITTEN]: 'high'
}

// SLA по умолчанию (в часах)
export const DEFAULT_SLA_HOURS: Record<TicketMainCategory, number> = {
  [TicketMainCategory.ACTIONS]: 24,
  [TicketMainCategory.CONSULTATIONS]: 8,
  [TicketMainCategory.SUGGESTIONS]: 72,
  [TicketMainCategory.COMPLAINTS_ORAL]: 48,
  [TicketMainCategory.COMPLAINTS_WRITTEN]: 72
}
